import { useEffect, useState } from 'react';
import { GA_AREA_KINDS, SECTION_TYPES as SECTION_TYPE_CODES } from '@bre-ticketing/contracts';
import { useMutation } from '@tanstack/react-query';
import { placeRowSeats } from '@bre-ticketing/seatmap/core';
import type { Point } from '@bre-ticketing/seatmap/core';
import type { ApiError, LevelRow, RowPatternInput, SectionRow } from '../../api';
import { endpoints } from '../../api';
import { Button, Field, Panel } from '../../ui';

/**
 * The forms the manifest editor opens. Each one owns its API call and reports
 * success or failure to the page, which refreshes the layout.
 */

export const INPUT = 'rounded border border-line bg-surface px-2 py-1.5 text-sm';

/**
 * The kinds themselves come from the contract, not from a copy (D-151).
 *
 * These were two hand-written arrays that had to be remembered whenever the
 * lookup tables gained a row, and one of them was not: `TIMED_ENTRY` was added
 * to `ga_area_kinds` in migration 0037 and to `GA_AREA_KINDS` in the contract,
 * and the dropdown here was never touched — so a timed-entry event could not be
 * drawn at all. The area had to exist before a schedule could hang slots off
 * it, and there was no way to make one.
 */
export const GA_KINDS = GA_AREA_KINDS;
export const SECTION_TYPES = SECTION_TYPE_CODES;

interface FormProps {
  manifestId: string;
  onCancel: () => void;
  onDone: () => void;
  onError: (error: ApiError) => void;
}

const digits = (value: string) => value.replace(/[^\d.-]/g, '');

export function NewLevel({ manifestId, onCancel, onDone, onError }: FormProps) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => endpoints.createLevel(manifestId, { code: code.trim().toUpperCase(), name: name.trim() }),
    onSuccess: onDone,
    onError,
  });
  return (
    <Panel title="Add a level" description="A floor of the building: Floor, Lower Balcony, Mezzanine. Sections are placed on one.">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Code" htmlFor="level-code">
          <input id="level-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="BALCONY" className={`w-40 ${INPUT}`} />
        </Field>
        <Field label="Name" htmlFor="level-name">
          <input id="level-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Balcony" className={`w-56 ${INPUT}`} />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add level'}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

export function NewSection({ manifestId, levels, onCancel, onDone, onError }: FormProps & { levels: LevelRow[] }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState(SECTION_TYPES[0] as string);
  const [levelId, setLevelId] = useState('');

  const create = useMutation({
    mutationFn: () =>
      endpoints.createSection(manifestId, {
          code: code.trim(),
          name: name.trim(),
          section_type: type,
          level_id: levelId || null,
        }),
    onSuccess: onDone,
    onError,
  });

  return (
    <Panel title="Add a section">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Code" htmlFor="section-code">
          <input id="section-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="101" className={`w-32 ${INPUT}`} />
        </Field>
        <Field label="Name" htmlFor="section-name">
          <input id="section-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Floor 101" className={`w-56 ${INPUT}`} />
        </Field>
        <Field label="Type" htmlFor="section-type">
          <select id="section-type" value={type} onChange={(e) => setType(e.target.value)} className={INPUT}>
            {SECTION_TYPES.map((value) => (
              <option key={value} value={value}>
                {value.replaceAll('_', ' ').toLowerCase()}
              </option>
            ))}
          </select>
        </Field>
        {levels.length > 0 ? (
          <Field label="Level" htmlFor="section-level">
            <select id="section-level" value={levelId} onChange={(e) => setLevelId(e.target.value)} className={INPUT}>
              <option value="">— none —</option>
              {levels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add section'}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

export function NewGaArea({ manifestId, sections, onCancel, onDone, onError }: FormProps & { sections: SectionRow[] }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState(GA_KINDS[0] as string);
  const [capacity, setCapacity] = useState('100');
  const [accessible, setAccessible] = useState('0');
  const [sectionId, setSectionId] = useState('');

  const create = useMutation({
    mutationFn: () =>
      endpoints.createGaArea(manifestId, {
          code: code.trim(),
          name: name.trim(),
          area_kind: kind,
          capacity: Number(capacity),
          accessible_capacity: Number(accessible || 0),
          section_id: sectionId || null,
        }),
    onSuccess: onDone,
    onError,
  });

  return (
    <Panel title="Add GA capacity">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Code" htmlFor="ga-code">
          <input id="ga-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="LAWN" className={`w-32 ${INPUT}`} />
        </Field>
        <Field label="Name" htmlFor="ga-name">
          <input id="ga-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Lawn" className={`w-48 ${INPUT}`} />
        </Field>
        <Field label="Kind" htmlFor="ga-kind">
          <select id="ga-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={INPUT}>
            {GA_KINDS.map((value) => (
              <option key={value} value={value}>
                {value.replaceAll('_', ' ').toLowerCase()}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Capacity" htmlFor="ga-capacity">
          <input id="ga-capacity" value={capacity} onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
        </Field>
        <Field label="Of which accessible" htmlFor="ga-accessible">
          <input id="ga-accessible" value={accessible} onChange={(e) => setAccessible(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
        </Field>
        {sections.length > 0 ? (
          <Field label="Within section" htmlFor="ga-section">
            <select id="ga-section" value={sectionId} onChange={(e) => setSectionId(e.target.value)} className={INPUT}>
              <option value="">— free-standing —</option>
              {sections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || Number(capacity) < 1 || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add area'}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

/**
 * Rows are described as a range and the seats follow. The first seat is
 * placed by clicking the map (or typing), and the row can be rotated and
 * curved; the preview count is what the API will write, because both use
 * `placeRowSeats`.
 */
export function GenerateRows({
  manifestId,
  sectionId,
  sectionName,
  placement,
  onRequestPlacement,
  onCancel,
  onDone,
  onError,
}: FormProps & {
  sectionId: string;
  sectionName: string;
  /** The last canvas click, when the operator asked to place by clicking. */
  placement: Point | null;
  onRequestPlacement: (active: boolean) => void;
}) {
  const [fromLabel, setFromLabel] = useState('A');
  const [toLabel, setToLabel] = useState('J');
  const [seatCount, setSeatCount] = useState('20');
  const [firstSeat, setFirstSeat] = useState('1');
  const [numbering, setNumbering] = useState<'SEQUENTIAL' | 'ODD' | 'EVEN'>('SEQUENTIAL');
  const [reverse, setReverse] = useState(false);
  const [rowSpacing, setRowSpacing] = useState('30');
  const [seatSpacing, setSeatSpacing] = useState('24');
  const [x, setX] = useState('100');
  const [y, setY] = useState('100');
  const [rotation, setRotation] = useState('0');
  const [curve, setCurve] = useState('0');
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (placement && placing) {
      setX(String(Math.round(placement[0])));
      setY(String(Math.round(placement[1])));
      setPlacing(false);
      onRequestPlacement(false);
    }
  }, [placement, placing, onRequestPlacement]);

  const labels = (): string[] => {
    const start = fromLabel.trim().toUpperCase().charCodeAt(0);
    const end = toLabel.trim().toUpperCase().charCodeAt(0);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
    return Array.from({ length: end - start + 1 }, (_, index) => String.fromCharCode(start + index));
  };
  const rows = labels();
  const totalSeats = rows.length * Number(seatCount || 0);

  const patterns = (): RowPatternInput[] =>
    rows.map((label, index) => {
      // Rows step back from the first along the row's normal, so a rotated
      // section stays a block rather than shearing.
      const angle = (Number(rotation || 0) * Math.PI) / 180;
      const offset = index * Number(rowSpacing || 0);
      return {
        label,
        seat_count: Number(seatCount),
        first_seat_number: Number(firstSeat || 1),
        numbering,
        reverse,
        x: Number(x) - Math.sin(angle) * offset,
        y: Number(y) + Math.cos(angle) * offset,
        seat_spacing: Number(seatSpacing || 24),
        rotation: Number(rotation || 0),
        curve_radius: Number(curve || 0),
      };
    });

  const generate = useMutation({
    mutationFn: () => endpoints.generateRows(manifestId, sectionId, patterns()),
    onSuccess: onDone,
    onError,
  });

  const preview =
    rows.length > 0
      ? placeRowSeats({
          seat_count: Number(seatCount || 0),
          x: Number(x),
          y: Number(y),
          seat_spacing: Number(seatSpacing || 24),
          rotation: Number(rotation || 0),
          curve_radius: Number(curve || 0),
        })
      : [];
  const spread = preview.length > 1 ? Math.round(preview[preview.length - 1]!.y - preview[Math.floor(preview.length / 2)]!.y) : 0;

  return (
    <Panel title={`Add rows to ${sectionName}`} description="Describe the rows; the seats are generated from the pattern.">
      <div className="px-5 py-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Rows" htmlFor="rows-from">
            <div className="flex items-center gap-1">
              <input id="rows-from" value={fromLabel} onChange={(e) => setFromLabel(e.target.value.slice(0, 2).toUpperCase())} className={`w-12 text-center ${INPUT}`} />
              <span className="text-sm text-ink-400">to</span>
              <input aria-label="last row" value={toLabel} onChange={(e) => setToLabel(e.target.value.slice(0, 2).toUpperCase())} className={`w-12 text-center ${INPUT}`} />
            </div>
          </Field>
          <Field label="Seats per row" htmlFor="rows-count">
            <input id="rows-count" value={seatCount} onChange={(e) => setSeatCount(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="First number" htmlFor="rows-first">
            <input id="rows-first" value={firstSeat} onChange={(e) => setFirstSeat(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="Numbering" htmlFor="rows-numbering">
            <select id="rows-numbering" value={numbering} onChange={(e) => setNumbering(e.target.value as 'SEQUENTIAL' | 'ODD' | 'EVEN')} className={INPUT}>
              <option value="SEQUENTIAL">sequential</option>
              <option value="ODD">odd only</option>
              <option value="EVEN">even only</option>
            </select>
          </Field>
          <label className="flex items-center gap-2 pb-1.5 text-sm text-ink-900">
            <input type="checkbox" checked={reverse} onChange={(e) => setReverse(e.target.checked)} />
            number right to left
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label="First seat X" htmlFor="rows-x">
            <input id="rows-x" value={x} onChange={(e) => setX(digits(e.target.value))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
          </Field>
          <Field label="Y" htmlFor="rows-y">
            <input id="rows-y" value={y} onChange={(e) => setY(digits(e.target.value))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
          </Field>
          <Button
            type="button"
            variant={placing ? 'primary' : 'secondary'}
            onClick={() => {
              setPlacing(!placing);
              onRequestPlacement(!placing);
            }}
          >
            {placing ? 'Click the map…' : 'Place by clicking the map'}
          </Button>
          <Field label="Row gap" htmlFor="rows-gap">
            <input id="rows-gap" value={rowSpacing} onChange={(e) => setRowSpacing(digits(e.target.value))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="Seat gap" htmlFor="seats-gap">
            <input id="seats-gap" value={seatSpacing} onChange={(e) => setSeatSpacing(digits(e.target.value))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="Rotation °" htmlFor="rows-rotation">
            <input id="rows-rotation" value={rotation} onChange={(e) => setRotation(digits(e.target.value))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="Curve radius" htmlFor="rows-curve" hint="0 is straight; larger is gentler; negative bows the other way">
            <input id="rows-curve" value={curve} onChange={(e) => setCurve(digits(e.target.value))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
          </Field>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <Button variant="primary" disabled={rows.length === 0 || totalSeats === 0 || generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generating…' : `Generate ${rows.length} row(s), ${totalSeats} seat(s)`}
          </Button>
          <Button onClick={onCancel} disabled={generate.isPending}>
            Cancel
          </Button>
          {rows.length === 0 ? <span className="text-xs text-bad">Last row must not come before the first.</span> : null}
          {Number(curve) !== 0 && preview.length > 1 ? (
            <span className="text-xs text-ink-400">Row ends sit {Math.abs(spread)} units {spread > 0 ? 'below' : 'above'} the middle.</span>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}

export function NewTable({
  manifestId,
  sectionId,
  sectionName,
  placement,
  onRequestPlacement,
  onCancel,
  onDone,
  onError,
}: FormProps & {
  sectionId: string;
  sectionName: string;
  placement: Point | null;
  onRequestPlacement: (active: boolean) => void;
}) {
  const [label, setLabel] = useState('1');
  const [sellMode, setSellMode] = useState<'WHOLE_TABLE' | 'BY_SEAT'>('BY_SEAT');
  const [shape, setShape] = useState<'ROUND' | 'RECT'>('ROUND');
  const [width, setWidth] = useState('60');
  const [height, setHeight] = useState('60');
  const [rotation, setRotation] = useState('0');
  const [seatCount, setSeatCount] = useState('8');
  const [firstSeat, setFirstSeat] = useState('1');
  const [x, setX] = useState('200');
  const [y, setY] = useState('200');
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (placement && placing) {
      setX(String(Math.round(placement[0])));
      setY(String(Math.round(placement[1])));
      setPlacing(false);
      onRequestPlacement(false);
    }
  }, [placement, placing, onRequestPlacement]);

  const create = useMutation({
    mutationFn: () =>
      endpoints.createTable(manifestId, sectionId, {
          label: label.trim(),
          sell_mode: sellMode,
          shape,
          x: Number(x),
          y: Number(y),
          width: Number(width),
          height: Number(height),
          rotation: Number(rotation || 0),
          seat_count: Number(seatCount),
          first_seat_number: Number(firstSeat || 1),
        }),
    onSuccess: onDone,
    onError,
  });

  return (
    <Panel title={`Add a table to ${sectionName}`} description="Seats are placed evenly around it, numbered clockwise from the top.">
      <form
        className="px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Table" htmlFor="table-label">
            <input id="table-label" value={label} onChange={(e) => setLabel(e.target.value)} className={`w-20 ${INPUT}`} />
          </Field>
          <Field label="Sold as" htmlFor="table-mode">
            <select id="table-mode" value={sellMode} onChange={(e) => setSellMode(e.target.value as 'WHOLE_TABLE' | 'BY_SEAT')} className={INPUT}>
              <option value="BY_SEAT">individual seats</option>
              <option value="WHOLE_TABLE">whole table</option>
            </select>
          </Field>
          <Field label="Shape" htmlFor="table-shape">
            <select id="table-shape" value={shape} onChange={(e) => setShape(e.target.value as 'ROUND' | 'RECT')} className={INPUT}>
              <option value="ROUND">round</option>
              <option value="RECT">rectangular</option>
            </select>
          </Field>
          <Field label="Seats" htmlFor="table-seats">
            <input id="table-seats" value={seatCount} onChange={(e) => setSeatCount(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-16 ${INPUT}`} />
          </Field>
          <Field label="First number" htmlFor="table-first">
            <input id="table-first" value={firstSeat} onChange={(e) => setFirstSeat(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-16 ${INPUT}`} />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label="Center X" htmlFor="table-x">
            <input id="table-x" value={x} onChange={(e) => setX(digits(e.target.value))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
          </Field>
          <Field label="Y" htmlFor="table-y">
            <input id="table-y" value={y} onChange={(e) => setY(digits(e.target.value))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
          </Field>
          <Button
            type="button"
            variant={placing ? 'primary' : 'secondary'}
            onClick={() => {
              setPlacing(!placing);
              onRequestPlacement(!placing);
            }}
          >
            {placing ? 'Click the map…' : 'Place by clicking the map'}
          </Button>
          <Field label="Width" htmlFor="table-w">
            <input id="table-w" value={width} onChange={(e) => setWidth(digits(e.target.value))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="Height" htmlFor="table-h">
            <input id="table-h" value={height} onChange={(e) => setHeight(digits(e.target.value))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="Rotation °" htmlFor="table-rot">
            <input id="table-rot" value={rotation} onChange={(e) => setRotation(digits(e.target.value))} inputMode="numeric" className={`tabular w-20 ${INPUT}`} />
          </Field>
        </div>
        <div className="mt-3 flex gap-2">
          <Button type="submit" variant="primary" disabled={!label.trim() || Number(seatCount) < 1 || Number(width) <= 0 || Number(height) <= 0 || create.isPending}>
            {create.isPending ? 'Adding…' : `Add table with ${seatCount || 0} seats`}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}
